import Plot from 'features/plotly'
import max from 'lodash/max'
import min from 'lodash/min'
import { ScanData } from 'pages/reporting/reports/raut/sections/subSections/indicationDetailsComponents/indicationDisplay/types'
import { Layout, PlotData } from 'plotly.js'
import React, { useMemo } from 'react'

/**
 * Color palette matching Maxweld's point cloud visualization
 * Converted from paut-display colorPalette.ts colorStops
 */
const MAXWELD_COLOR_SCALE: Array<[number, string]> = [
  [0.0, 'rgb(255, 255, 255)'], // White
  [0.059, 'rgb(188, 215, 244)'], // Light blue
  [0.145, 'rgb(105, 166, 232)'], // Medium blue
  [0.212, 'rgb(48, 85, 176)'], // Darker blue
  [0.282, 'rgb(3, 21, 132)'], // Deep blue
  [0.306, 'rgb(18, 52, 130)'], // Navy blue
  [0.341, 'rgb(35, 88, 129)'], // Blue-gray
  [0.408, 'rgb(65, 152, 126)'], // Teal
  [0.424, 'rgb(76, 166, 123)'], // Sea green
  [0.486, 'rgb(156, 199, 77)'], // Light green
  [0.565, 'rgb(250, 236, 25)'], // Yellow
  [0.624, 'rgb(232, 185, 60)'], // Gold
  [0.69, 'rgb(213, 130, 98)'], // Light orange
  [0.757, 'rgb(207, 119, 71)'], // Orange
  [0.855, 'rgb(198, 106, 23)'], // Dark orange
  [0.925, 'rgb(167, 50, 26)'], // Red-orange
  [1.0, 'rgb(145, 12, 29)'], // Deep red
]

interface WeldHeatmapPlotProps {
  /** Scan data (2D array) */
  data: ScanData
  /** Plot width in pixels */
  width: number
  /** Plot height in pixels */
  height: number
  /** Whether this is a static plot */
  isStatic?: boolean
  /** Title to display */
  title?: string
  /** X-axis label */
  xAxisLabel?: string
  /** Y-axis label */
  yAxisLabel?: string
  /** Show color scale bar */
  showColorScale?: boolean
  /** Z index for vertical crosshair (array column index) */
  zIndex?: number
  /** Show vertical crosshair */
  showCrosshair?: boolean
}

/**
 * WeldHeatmapPlot - Generic heatmap for weld top view and side view visualization
 *
 * Uses the same color palette as Maxweld's point cloud visualization
 */
export const WeldHeatmapPlot: React.FC<WeldHeatmapPlotProps> = ({
  data,
  width,
  height,
  isStatic = true,
  title,
  xAxisLabel = '',
  yAxisLabel = '',
  showColorScale = false,
  zIndex,
  showCrosshair = false,
}) => {
  const plotData = useMemo(() => {
    if (!data?.data || data.data.length === 0) {
      return [
        {
          z: [[0]],
          type: 'heatmap',
          colorscale: MAXWELD_COLOR_SCALE,
          showscale: false,
          zmin: 0,
          zmax: 1,
        } as Partial<PlotData>,
      ]
    }

    const flatData = data.data.flat().filter((val) => val !== 0)

    if (flatData.length === 0) {
      return [
        {
          z: [[0]],
          type: 'heatmap',
          colorscale: MAXWELD_COLOR_SCALE,
          showscale: false,
          zmin: 0,
          zmax: 1,
        } as Partial<PlotData>,
      ]
    }

    const minValue = min(flatData) ?? 0
    const maxValue = max(flatData) ?? 100

    const traces: Partial<PlotData>[] = [
      {
        z: data.data,
        type: 'heatmap',
        colorscale: MAXWELD_COLOR_SCALE,
        showscale: showColorScale,
        zmin: minValue,
        zmax: maxValue,
      } as Partial<PlotData>,
    ]

    // Add vertical crosshair line at z index if enabled
    if (showCrosshair && zIndex !== undefined) {
      const rows = data.data.length
      const cols = data.data[0]?.length || 0

      // Only draw if within bounds
      if (zIndex >= 0 && zIndex < cols) {
        // Vertical line spanning the full height
        traces.push({
          x: [zIndex, zIndex],
          y: [0, rows - 1],
          mode: 'lines',
          line: { color: 'black', width: 1 },
          showlegend: false,
          hoverinfo: 'skip',
        } as Partial<PlotData>)
      }
    }

    return traces
  }, [data, showColorScale, zIndex, showCrosshair])

  const layout = useMemo(
    () =>
      ({
        width,
        height,
        margin: {
          t: title ? 25 : 5,
          r: 10,
          b: xAxisLabel ? 30 : 10,
          l: yAxisLabel ? 35 : 10,
        },
        plot_bgcolor: '#ffffff',
        paper_bgcolor: '#ffffff',
        title: title
          ? {
              text: title,
              font: { size: 10 },
              x: 0.5,
              y: 0.98,
            }
          : undefined,
        xaxis: {
          title: xAxisLabel
            ? {
                text: xAxisLabel,
                font: { size: 8 },
                standoff: 3,
              }
            : undefined,
          showticklabels: false,
          showgrid: false,
          fixedrange: true,
        },
        yaxis: {
          title: yAxisLabel
            ? {
                text: yAxisLabel,
                font: { size: 8 },
                standoff: 3,
              }
            : undefined,
          showticklabels: false,
          showgrid: false,
          fixedrange: true,
        },
      }) as Partial<Layout>,
    [width, height, title, xAxisLabel, yAxisLabel]
  )

  const config = useMemo(
    () => ({
      displayModeBar: !isStatic,
      staticPlot: isStatic,
    }),
    [isStatic]
  )

  return <Plot data={plotData} layout={layout} config={config} />
}

/**
 * WeldTopViewPlot - Top view for weld indications
 * Shows cross-weld (X) vs along-weld (Z) position
 */
export const WeldTopViewPlot: React.FC<
  Omit<WeldHeatmapPlotProps, 'xAxisLabel' | 'yAxisLabel'>
> = (props) => {
  return (
    <WeldHeatmapPlot
      {...props}
      title={props.title ?? 'Top View'}
      xAxisLabel='Along-Weld (Z)'
      yAxisLabel='Cross-Weld (X)'
    />
  )
}

/**
 * WeldSideViewPlot - Side view for weld indications
 * Shows depth (Y) vs along-weld (Z) position
 */
export const WeldSideViewPlot: React.FC<
  Omit<WeldHeatmapPlotProps, 'xAxisLabel' | 'yAxisLabel'>
> = (props) => {
  return (
    <WeldHeatmapPlot
      {...props}
      title={props.title ?? 'Side View'}
      xAxisLabel='Along-Weld (Z)'
      yAxisLabel='Depth (Y)'
    />
  )
}
